import { useQuery, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import { MemberType } from "src/types";
import { members } from "../components/templates/design1/components";
import { getClientDomain } from "./useFetchUnits";
import { logEnvironment } from "@components/lib/utils";

// Raw API response types
interface RawStaff {
  id: number;
  name: string;
  title: string;
  tier: number;
  estate_title: string;
  professional_title: string;
  staff_role: string;
  email: string;
  phone: string;
  username: string | null;
  picture: string | null;
  user_id: number;
  branch: string;
  company_id: number;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total_pages: number;
}

interface StaffResponse {
  status: "success" | "error";
  staff: RawStaff[];
  pagination: Pagination;
  total_data_count: number;
  message: string;
}

interface FetchCompanyStaffResult {
  staff: MemberType[];
  totalPages: number;
  totalCount: number;
}

// Configuration
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL as string;
const COMPANY_IDENTIFIER = getClientDomain();
logEnvironment("this the fetch company staff componennt");

// **Fetch a Single Page**
const fetchCompanyStaffPage = async (
  page: number,
  perPage: number,
  location?: string
): Promise<StaffResponse> => {
  if (!COMPANY_IDENTIFIER && !location) return {} as unknown as StaffResponse;
  try {
    const response = await axios.get<StaffResponse>(
      `${BASE_URL}api/v1/custom/company-staff/${COMPANY_IDENTIFIER || location
      }`,
      {
        params: { page, per_page: perPage },
      }
    );
    if (response.data.status !== "success") {
      throw new Error(response.data.message || "Failed to fetch staff");
    }
    console.log(response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message || "Network error while fetching staff"
      );
    }
    throw new Error("An unexpected error occurred while fetching staff");
  }
};
export function cleanAndCapitalize(str: string): string {
  if (!str) return ""; // handle null, undefined, or empty input

  return str
    .replace(/null/gi, "") // remove "null" (case-insensitive)
    .trim() // remove leading/trailing spaces
    .split(/\s+/) // split by whitespace
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// **Fetch All Pages Concurrently**
export const fetchAllCompanyStaff = async (
  perPage: number = 20,
  location?: string
): Promise<FetchCompanyStaffResult> => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  if (isDemo) {
    return {
      staff: members,
      totalPages: 1,
      totalCount: members.length,
    };
  }
  try {
    const firstPageResponse = await fetchCompanyStaffPage(1, perPage, location);
    const firstPageStaff = firstPageResponse.staff.map((staff: RawStaff) => ({
      id: staff.user_id,
      name: cleanAndCapitalize(`${staff.title} ${staff.name}`),
      title: staff.title,
      estateTitle: staff.estate_title,
      role: staff.professional_title,
      email: staff.email,
      phone: staff.phone,
      is_verified: staff.tier == 2,
      username: staff.username,
      image: staff.picture,
      userId: staff.user_id,
      branch: staff.branch,
      companyId: staff.company_id,
    }));
    const totalPages = firstPageResponse.pagination.total_pages;
    const totalCount = firstPageResponse.total_data_count;
    if (totalPages === 1) {
      return {
        staff: firstPageStaff,
        totalPages: 1,
        totalCount,
      };
    }
    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, index) => index + 2 // Pages 2, 3, ..., totalPages
    );
    const remainingResponses = await Promise.all(
      remainingPages.map((page) => fetchCompanyStaffPage(page, perPage))
    );
    const remainingStaff = remainingResponses.flatMap((response) =>
      response?.staff?.map((staff: RawStaff) => ({
        id: staff.id,
        name: cleanAndCapitalize(`${staff.title} ${staff.name}`),
        title: staff.title,
        estateTitle: staff.estate_title,
        role: staff.professional_title,
        email: staff.email,
        phone: staff.phone,
        is_verified: staff.tier == 2,
        username: staff.username,
        image: staff.picture,
        userId: staff.user_id,
        branch: staff.branch,
        companyId: staff.company_id,
      }))
    );
    const allStaff = [...firstPageStaff, ...remainingStaff];
    console.log("all staff", { allStaff });
    return {
      staff: allStaff,
      totalPages,
      totalCount,
    };
  } catch (error) {
    console.error("Error fetching company staff:", error);
    throw error;
  }
};

// **React Query Hook to Fetch All Data at Once**
export const useCompanyStaff = (): UseQueryResult<
  FetchCompanyStaffResult,
  Error
> => {
  return useQuery({
    queryKey: ["companyStaff"],
    queryFn: () => fetchAllCompanyStaff(),
    staleTime: 1000 * 60 * 5, // Cache data for 5 minutes
    retry: 2,
  });
};
